/**
 * Agency (super-admin) API client.
 *
 * Wraps the cross-tenant /api/admin/super/* + snapshot endpoints that already
 * exist on the backend (api/admin.py, api/snapshot.py) behind get_super_admin_user.
 * These are the data sources for the Agency dashboard: list every client tenant,
 * read a tenant's config, and provision a new client by cloning a reference
 * tenant's config (export -> import). Protected tenants (dvw4ea / buildgravity)
 * are rejected server-side before any write.
 */
import { auth } from '../firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function getJson<T>(path: string): Promise<T> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(extractDetail(err) || `Request failed: ${res.statusText}`);
  }
  return res.json();
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(extractDetail(err) || `Request failed: ${res.statusText}`);
  }
  return res.json();
}

async function putJson<T>(path: string, body: unknown): Promise<T> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(extractDetail(err) || `Request failed: ${res.statusText}`);
  }
  return res.json();
}

/**
 * Pull a human-readable message out of a FastAPI error body. A protected-tenant
 * refusal comes back as { detail: { error, marker: "live_tenant_safety" } }; a
 * validation error as { detail: "..." }. Never surfaces a raw object to the UI.
 */
function extractDetail(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const detail = (err as { detail?: unknown }).detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const obj = detail as { error?: unknown; marker?: unknown };
    if (typeof obj.error === 'string') {
      return obj.marker === 'live_tenant_safety'
        ? `Blocked: ${obj.error} (protected live tenant)`
        : obj.error;
    }
  }
  return undefined;
}

export interface AgencyTenant {
  tenant_id: string;
  name: string;
  user_count: number;
}

export interface AgencyTenantConfig {
  tenant_id: string;
  name: string;
  modules: Record<string, boolean>;
}

export interface AgencyUser {
  user_doc_id?: string;
  email: string;
  name?: string;
  is_super_admin?: boolean;
  default_tenant?: string;
  tenant_memberships?: string[];
}

export interface TenantDetail {
  integrations?: Record<string, unknown>;
  dashboard_config?: Record<string, unknown>;
  modules?: Record<string, boolean>;
  [key: string]: unknown;
}

export async function listAllTenants(): Promise<AgencyTenant[]> {
  const data = await getJson<{ tenants: AgencyTenant[] }>('/api/admin/super/tenants');
  return data.tenants || [];
}

export async function listTenantConfigs(): Promise<AgencyTenantConfig[]> {
  const data = await getJson<{ configs: AgencyTenantConfig[] }>('/api/admin/super/tenants/configs');
  return data.configs || [];
}

export async function listAllUsers(): Promise<AgencyUser[]> {
  const data = await getJson<{ users: AgencyUser[] }>('/api/admin/super/users');
  return data.users || [];
}

export async function getTenantDetail(tenantId: string): Promise<TenantDetail> {
  return getJson<TenantDetail>(`/api/admin/tenant/${tenantId}/config`);
}

/** Setup read-back from the same tenant store as the setup writers.
 * Credentials are omitted, never replaced with editable masking strings.
 */
export async function getTenantSetupConfig(tenantId: string): Promise<TenantDetail> {
  return getJson<TenantDetail>(`/api/admin/super/tenants/${encodeURIComponent(tenantId)}/config`);
}

/** Export a reference tenant's config blob (safe on any tenant). */
export async function exportSnapshot(tenantId: string): Promise<unknown> {
  return postJson<unknown>('/api/admin/snapshot/export', { tenant_id: tenantId });
}

/** Provision/seed a NEW tenant from a snapshot blob. Protected targets rejected server-side. */
export async function importSnapshot(
  targetTenantId: string,
  snapshotBlob: unknown,
  overwrite = false,
): Promise<unknown> {
  return postJson<unknown>('/api/admin/snapshot/import', {
    target_tenant_id: targetTenantId,
    snapshot_blob: snapshotBlob,
    overwrite,
  });
}

/** Provision = export a reference tenant then import into the new tenant id. */
export async function provisionFromReference(
  referenceTenantId: string,
  newTenantId: string,
  overwrite = false,
): Promise<unknown> {
  const blob = await exportSnapshot(referenceTenantId);
  return importSnapshot(newTenantId, blob, overwrite);
}

// ===========================================================================
// Tenant Setup Console writers (SPEC tenant-config-platform R3).
//
// Wire the console editor to the EXISTING /api/admin/super/* write endpoints
// (admin.py) - identity/modules, integrations (incl. tracking ids + branding),
// CORS origins, cal config, the secrets framework (R2), the go-live toggles
// (setter_agent.send_enabled / agent_paused), and the config-change rebuild
// dispatch. These do not duplicate the backend - they call it. Protected tenants
// (dvw4ea/buildgravity) are refused server-side BEFORE any write (Rule 0).
// ===========================================================================

export interface TenantBranding {
  display_name?: string;
  logo_url?: string;
  primary_color?: string;
  accent_color?: string;
  custom_domain?: string;
  favicon_url?: string;
  [key: string]: unknown;
}

/** Read a tenant's branding (defaults filled) for the editor prefill. */
export async function getTenantBranding(tenantId: string): Promise<TenantBranding> {
  const data = await getJson<{ branding: TenantBranding }>(
    `/api/admin/super/tenants/${tenantId}/branding`,
  );
  return data.branding || {};
}

/** Create/update a tenant's branding (config strings only; reuses the service). */
export async function saveTenantBranding(
  tenantId: string,
  branding: TenantBranding,
): Promise<{ success: boolean; branding: TenantBranding }> {
  return putJson(`/api/admin/super/tenants/${tenantId}/branding`, branding);
}

/** Identity + module config (user_db tenants doc). */
export async function saveTenantConfig(
  tenantId: string,
  name: string,
  modules: Record<string, boolean>,
): Promise<{ success: boolean }> {
  return putJson(`/api/admin/super/tenants/${tenantId}/config`, { name, modules });
}

/**
 * Integrations + dashboard config (user_db tenants doc). Branding + tracking ids
 * (meta pixel, ga4) + integration credentials all live under `integrations`.
 * Integration secrets are encrypted server-side and never returned.
 */
export async function saveTenantIntegrations(
  tenantId: string,
  payload: { integrations?: Record<string, unknown>; dashboard_config?: Record<string, unknown> },
): Promise<{ success: boolean }> {
  return putJson(`/api/admin/super/tenants/${tenantId}/integrations`, payload);
}

/** Per-tenant CORS allowed origins (no-redeploy custom-origin path). */
export async function saveTenantOrigins(
  tenantId: string,
  origins: string[],
): Promise<{ success: boolean; allowed_origins: string[] }> {
  return putJson(`/api/admin/super/tenants/${tenantId}/allowed-origins`, {
    allowed_origins: origins,
  });
}

/** Cal.com config (cal_embed_url is public; cal_webhook_secret is write-only). */
export async function saveTenantCalConfig(
  tenantId: string,
  payload: { cal_embed_url?: string; cal_webhook_secret?: string },
): Promise<{ success: boolean }> {
  return putJson(`/api/admin/super/tenants/${tenantId}/cal-config`, payload);
}

export interface SetterConfigState {
  tenant_id: string;
  setter_agent: Record<string, unknown>;
  send_enabled: boolean;
  agent_paused: boolean;
}

/** Read the stored setter_agent block + resolved go-live toggles (for prefill). */
export async function getSetterConfig(tenantId: string): Promise<SetterConfigState> {
  return getJson<SetterConfigState>(`/api/admin/super/tenants/${tenantId}/setter-config`);
}

/**
 * Write the setter_agent block (the read-modify-write of the go-live toggles).
 * Pass the WHOLE block back so persona/kb/etc. are preserved; send_enabled is
 * resolved STRICTLY server-side (dead-by-default - only a literal true enables).
 */
export async function saveSetterConfig(
  tenantId: string,
  setterAgent: Record<string, unknown>,
): Promise<{ success: boolean; send_enabled: boolean }> {
  return putJson(`/api/admin/super/tenants/${tenantId}/setter-config`, {
    setter_agent: setterAgent,
  });
}

export interface SecretStatus {
  // The R2 status surface keys each entry by `name`; the set/rotate PUT response
  // echoes `secret_name`. Both optional so one type covers both shapes.
  name?: string;
  secret_name?: string;
  status: 'set' | 'unset';
  last_rotated_at: string | null;
  rotations: number;
}

/**
 * List a tenant's managed secrets as presence + last-rotated, NEVER the value
 * (R2 write-only / never-reveal). Shape mirrors the backend status surface.
 */
export async function listTenantSecrets(
  tenantId: string,
): Promise<SecretStatus[]> {
  const data = await getJson<{ secrets?: SecretStatus[] } & Record<string, unknown>>(
    `/api/admin/super/tenants/${tenantId}/secrets`,
  );
  // The R2 status surface returns { secrets: [...] } (or a flat list fallback).
  if (Array.isArray(data)) return data as SecretStatus[];
  if (Array.isArray(data.secrets)) return data.secrets;
  return [];
}

/**
 * Set or rotate ONE named secret (write-only). The value is encrypted server-side
 * via the R2 framework and never returned. `rotate` only labels the audit action.
 */
export async function setTenantSecret(
  tenantId: string,
  secretName: string,
  value: string,
  rotate = false,
): Promise<SecretStatus> {
  return putJson<SecretStatus>(
    `/api/admin/super/tenants/${tenantId}/secrets/${encodeURIComponent(secretName)}`,
    { value, rotate },
  );
}

/** Trigger a per-tenant static-site rebuild (metabus-apps workflow_dispatch). */
export async function dispatchTenantRebuild(
  tenantId: string,
): Promise<{ success: boolean; tenant_id: string; repo: string; workflow: string }> {
  return postJson(`/api/admin/super/tenants/${tenantId}/rebuild`, {});
}

export interface PublicTenantConfig {
  tenant_id: string;
  branding?: { display_name?: string; logo_url?: string; primary_color?: string; accent_color?: string };
  tracking?: { meta_pixel_id?: string; ga4_measurement_id?: string };
  cal_public_link?: string;
  enabled_views?: string[];
  client_label?: string | null;
  [key: string]: unknown;
}

/**
 * The aggregated PUBLIC config slice (R1) - the canonical read-back of what a
 * tenant's static site sees (branding + tracking ids + cal link). Used to prove
 * a console edit reflected (AC3) and to show what will be baked at rebuild.
 * Authless endpoint; works for the super-admin operator too.
 */
export async function getPublicTenantConfig(tenantId: string): Promise<PublicTenantConfig> {
  return getJson<PublicTenantConfig>(`/api/public/tenant/${tenantId}/config`);
}
